<script setup>
import { formatTelLink } from '@/composables/useFormatTelLink';
import { useGlobalStore } from '@/stores/global';
import FormConsultationWide from '@/components/forms/FormConsultationWide.vue';
import FormVacancy from '@/components/forms/FormVacancy.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  isUkravtogroup: {
    type: Boolean,
    default: () => false,
  },
});

const globalStore = useGlobalStore();

const displayTitle = computed(() => {
  return props.data?.title || globalStore.applicationData?.title || '';
});
const contactsData = computed(() => {
  const storeData = globalStore.applicationData || {};
  const propsContacts = props.data?.contacts || {};
  const getValidPhones = (phones) => {
    return Array.isArray(phones) && phones.length > 0 ? phones : null;
  };

  const finalPhones =
    getValidPhones(propsContacts.phones) ||
    getValidPhones(storeData.phones) ||
    [];

  return {
    title: propsContacts.title || storeData.subtitle || '',
    text: propsContacts.text || storeData.text || '',
    phones: finalPhones,
  };
});
const hasContacts = computed(() => {
  return (
    contactsData.value.title ||
    contactsData.value.text ||
    contactsData.value.phones.length > 0
  );
});
</script>
<template>
  <section
    :id="data.id"
    class="section white"
    :class="[
      data?.spacing?.top ? `section--top-${data.spacing.top}` : '',
      data?.spacing?.bottom ? `section--bottom-${data.spacing.bottom}` : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div
      class="application__background"
      :style="{
        'background-image': 'url(' + data?.background_image?.src + ')',
      }"
      :alt="data?.background_image?.alt"
    ></div>
    <div class="container">
      <div class="application">
        <div class="application__content">
          <h2
            v-if="displayTitle"
            class="mb-0 application__title display"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="displayTitle"
          ></h2>
          <div
            class="application__form"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          >
            <FormVacancy
              v-if="data.type === 'job' || data.type === 'carrier'"
              :background-color="data?.background_color"
            >
              <p
                v-if="globalStore.termsLink || globalStore.policyLink"
                class="mb-0 application__form-text body-3-regular"
              >
                {{ $t('vacancy.agreement_start') }}
                {{ $t('vacancy.send_button')
                }}{{ $t('vacancy.agreement_middle') }}
                <CTagDetect
                  v-if="globalStore.termsLink"
                  :to="globalStore.termsLink"
                  class="text-decoration-underline"
                >
                  {{ $t('vacancy.agreement_terms_of_use') }}
                </CTagDetect>
                <template v-else>
                  {{ $t('vacancy.agreement_terms_of_use') }}
                </template>
                {{ $t('vacancy.agreement_between') }}
                <CTagDetect
                  v-if="globalStore.policyLink"
                  :to="globalStore.policyLink"
                  class="text-decoration-underline"
                >
                  {{ $t('vacancy.agreement_privacy_policy') }}
                </CTagDetect>
                <template v-else>
                  {{ $t('vacancy.agreement_privacy_policy') }}
                </template>
              </p>
            </FormVacancy>
            <FormConsultationWide v-else :type="data.type">
              <p
                v-if="globalStore.termsLink || globalStore.policyLink"
                class="mb-0 application__form-text body-3-regular"
              >
                {{ $t('application.agreement_start') }}
                <template v-if="data.type === 'feedback'">
                  {{ $t('application.send') }}
                </template>
                <template v-else> {{ $t('application.send_button') }} </template
                >{{ $t('application.agreement_middle') }}
                <CTagDetect
                  v-if="globalStore.termsLink"
                  :to="globalStore.termsLink"
                  class="text-decoration-underline"
                >
                  {{ $t('application.agreement_terms_of_use') }}
                </CTagDetect>
                <template v-else>
                  {{ $t('application.agreement_terms_of_use') }}
                </template>
                {{ $t('application.agreement_between') }}
                <CTagDetect
                  v-if="globalStore.policyLink"
                  :to="globalStore.policyLink"
                  class="text-decoration-underline"
                >
                  {{ $t('application.agreement_privacy_policy') }}
                </CTagDetect>
                <template v-else>
                  {{ $t('application.agreement_privacy_policy') }}
                </template>
              </p>
            </FormConsultationWide>
          </div>
          <div v-if="hasContacts" class="application__contacts">
            <div
              class="application__contacts-top"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
            >
              <p
                v-if="contactsData.title"
                class="mb-0 application__contacts-title body-3-regular"
                v-html="contactsData.title"
              ></p>

              <a
                v-for="phone in contactsData.phones"
                :key="phone"
                :href="`tel:${formatTelLink(phone)}`"
                class="application__contacts-phone h4"
              >
                <CIcon
                  class="application__contacts-phone-icon"
                  name="phone-red"
                  width="24"
                  height="24"
                />{{ phone }}</a
              >
            </div>
            <p
              v-if="contactsData.text"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              class="mb-0 application__contacts-text body-3-regular"
              v-html="contactsData.text"
            ></p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.application {
  position: relative;
  @include media-breakpoint-up(sm) {
    padding: 0 60px;
  }
  @include media-breakpoint-up(md) {
    padding: 0;
  }
  &__background {
    background: no-repeat center center/cover;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    height: calc(100% + 160px);
    left: -16px;
    overflow: hidden;
    position: absolute;
    top: -80px;
    width: calc(100% + 32px);
    z-index: 0;
    &::before {
      background-color: rgba($black, 0.9);
      content: '';
      height: 100%;
      left: 0;
      top: 0;
      position: absolute;
      width: 100%;
      z-index: 1;
    }
    @include media-breakpoint-up(sm) {
      height: calc(100% + 240px);
      left: -20px;
      top: -120px;
      width: calc(100% + 40px);
    }
    @include media-breakpoint-up(md) {
      left: -48px;
      width: calc(100% + 96px);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(left, -48);
      @include responsive-spacing(top, -120);
      height: calc(100% + px-to-vw(240));
      width: calc(100% + px-to-vw(96));
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 56px;
    position: relative;
    z-index: 2;
    @include media-breakpoint-up(sm) {
      gap: 80px;
    }
    @include media-breakpoint-up(md) {
      align-items: start;
      display: grid;
      gap: 48px;
      grid-template-columns: repeat(2, 6fr);
      grid-template-areas: 'title form' 'contacts form';
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__title {
    grid-area: title;
    margin: 0;
    text-align: center;
    @include media-breakpoint-up(md) {
      text-align: left;
    }
  }
  &__form {
    display: flex;
    grid-area: form;
    flex-direction: column;
    &-text {
      padding-top: 24px;
      text-align: center;
      @include media-breakpoint-up(sm) {
        padding-top: 32px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 32);
      }
      @include media-breakpoint-up(md) {
        text-align: left;
      }
    }
  }
  &__contacts {
    display: flex;
    gap: 24px;
    grid-area: contacts;
    flex-direction: column;
    text-align: center;
    @include media-breakpoint-up(md) {
      gap: 32px;
      text-align: left;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 32);
    }
    &-top {
      display: flex;
      flex-direction: column;
      gap: 8px;
      @include media-breakpoint-up(md) {
        gap: 16px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
      }
    }
    &-phone {
      align-items: center;
      align-self: center;
      display: grid;
      gap: 16px;
      grid-template-columns: 24px auto;
      max-width: fit-content;
      @include media-breakpoint-up(md) {
        align-self: self-start;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
        grid-template-columns: px-to-vw(24) auto;
      }
      &-icon {
        height: 100%;
        width: 100%;
      }
    }
    &-text {
      @include media-breakpoint-up(md) {
        max-width: 49%;
      }
    }
  }
}
</style>
